import * as PIXI from "pixi.js";
import anime from "animejs";
import { symbolData, cellZIndex } from "../../../SymbolData";
import { formatToCustomLocale } from "../../../utilities";
import { Settings } from "../../../settings";
import {
  COIN_SYMBOL_IDS,
  SPIN_FILLER_SYMBOL_IDS,
  ROYAL_SYMBOL_ID,
  isCoinSymbol,
  isJackpotSymbol,
  getSymbolZIndex,
} from "../../../SymbolData";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import SpineAnimationData from "../../../utils/SpineAnimationData";
import { setSpinComplete, incrementWindupCount } from "../../../State/actions";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import store from "../../../State/Store/store";
import { isTurboLike, isSuperTurbo } from "../../../utils/speedHelpers";
import { COIN_JACKPOT_SYMBOL_ID, JACKPOT_SKIN_RANK } from "../../../SymbolData";
import { createCoinLabel, setCoinLabel } from "../../HoldAndWin/coinLabel";
import { cosmeticCoinValue, coinValueAt, coinValueBet } from "../../Shared/coinValues";
import {
  formatCoinValue,
  warmCoinValueLabels,
  COIN_LABEL_MAX_WIDTH_RATIO,
} from "../../Shared/coinValueLabel";
import SpineTextureExtractor from "../../../utils/SpineTextureExtractor";
import { jackpotLandSound } from "../../Shared/jackpotCoinSkin";

// Dynamic sprite count formula: 1 top buffer + N visible + N landing zone + 1 bottom buffer
const SPRITE_COUNT = Settings.SYMBOLS_VISIBLE * 2 + 2;
const VISIBLE_SYMBOL_COUNT = Settings.SYMBOLS_VISIBLE;
// Visible symbols start after the top buffer (1 sprite) + landing zone (N sprites)
const VISIBLE_SYMBOL_START_INDEX = 1 + Settings.SYMBOLS_VISIBLE;
// Stop animation moves sprites down by the landing zone distance (N symbol heights)
const STOP_MOVE_DISTANCE_SYMBOLS = Settings.SYMBOLS_VISIBLE;

// Symbols shown while spinning: what the GDD and BE allow to land
// (see SPIN_FILLER_SYMBOL_IDS in SymbolData).
const AVAILABLE_SYMBOL_IDS = SPIN_FILLER_SYMBOL_IDS;

// Coin value y: the `value` bone's y in c.json (skeleton units). The static
// number must sit exactly where the live spine's slot puts it, or it jumps at
// the handover. Same formula as the Golden Mystery flat cell
// (COIN_VALUE_OFFSET_Y / buildCellNumber):
//   y = -COIN_VALUE_Y_OFFSET * (symbolHeight / Settings.SYMBOL_HEIGHT)
// Negated because PIXI is Y-down.
const COIN_VALUE_Y_OFFSET = 0.13;

// Base game reel controller: wind-up, spin and stop animations with sprite wrapping.
class ReelControllerV2 {
  constructor(
    dispatch,
    spinningContainer,
    spriteSheet,
    spriteSheetBlurred,
    reels,
    reelIndex,
    symbolWidth,
    symbolHeight,
    onReelStop = null, // Callback when reel stops (for landing animations)
    autoPlaySpeed = "auto", // Current autoplay speed mode
    bet = 1, // Current bet value for coin display
    reelXOffset = 0, // This reel's column x, carried per-sprite (see below)
  ) {
    this.dispatch = dispatch;
    this.spinningContainer = spinningContainer;
    // Reel-level vertical offset, added to each sprite's y. Only the wind-up
    // tween drives it; the spin loop moves sprites individually.
    this.reelYOffset = 0;
    this._reelYTweenTarget = { y: 0 };
    // All reels share ONE container so their sprites z-sort together, so the
    // column x is stamped on each sprite.
    this.reelXOffset = reelXOffset;
    this.reelIndex = reelIndex;
    this.spritesheet = spriteSheet;
    this.spritesheetBlurred = FEATURES_CONFIG.SPIN.BLURRED_TEXTURES_ENABLED
      ? spriteSheetBlurred
      : null;
    // symbolId -> { normal, blurred }, resolved ONCE. See _buildTextureTable.
    this._textureTable = this._buildTextureTable();
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.reels = reels || []; // Store API landing data (array of symbol IDs)
    this.hasPendingLandingSymbols = false; // Flag to indicate API data is ready
    this.onReelStop = onReelStop; // Callback for landing animations
    this.onReelLanded = null; // Callback when reel has fully landed (bounce complete, after onReelStop)
    // Injected by Reel.jsx from useReelLandingAnimations. See _completeSpin.
    this.waitForBoardSettle = null;
    this.onLandingPeak = null; // Callback when reel hits overshoot trough (lowest visual point, before bounce-back)
    this.autoPlaySpeed = autoPlaySpeed; // Current speed setting
    this.lockedSpinSpeed = null; // Locked speed for current spin (prevents mid-spin changes)
    this.bet = bet || 1; // Store bet value for coin calculations

    // Animation state
    this.isPlaying = false;
    this.hasStopped = false;
    this.isStopping = false;
    this._wasManualStopped = false; // True if this reel was force-stopped by manual stop
    this.isTensionMode = false;
    this.isLastNonTensionReel = false; // True when this reel is the last to stop before tension reels begin
    this.hasTensionThisSpin = false; // True when tension is active this spin (set by context, used by landing hook)
    this.tickerCallback = null;
    this.spinningSprites = [];
    this._spinElapsed = 0; // Accumulated spin time (ms) — advances only when ticker runs
    this.normalizedSpinSpeed = 0;
    this.lastCalculatedPosition = 0;
    this.pendingLandingSymbolId = null; // Deprecated: use this.reels array instead
    this.isBlurred = false;
    this.visibleSprites = [];
    this.hasInitializedTextures = false;
    this.stopTweenActive = false;
    this.activeStopAnimation = null;
    this.stopAnimationProgress = 0;
    this.isWindingUp = false;
    this.windUpAnimation = null;
    // Which stop was requested while the wind-up was still running, if any.
    // "normal" | "turbo" | "tension" | null. See _deferStopDuringWindUp.
    this._pendingStopKind = null;
    this.speedMultiplier = 1.0;
    this.hasWinThisSpin = false; // Track if this spin has a win (affects sound playback)
    this.isHoldAndWinTrigger = false; // Track if this spin triggers Hold & Win (6+ coins)
    this.isExpandingWildActive = false; // Lock flag to prevent texture updates during expanding wild animation

    // Premium feel: acceleration, deceleration, velocity-based blur
    this.accelerationMultiplier = 1.0; // Ramps 0→1 during spin start
    this.currentVelocity = 0; // Effective velocity as fraction of max (0-1), used for blur
    this.isDecelerating = false; // True during phase 1 of 2-phase stop

    // Tension deceleration state (smooth slowdown when entering tension mode)
    this._tensionDecelerating = false; // True while reel is decelerating for tension
    this._tensionDecelElapsed = 0; // Accumulated tension deceleration time (ms)
    this._tensionDecelerationDuration = 0; // Computed at spinTension(): cycleDuration × DECELERATION_RATIO
    this._tensionSpeedMultiplier = 1.0; // Current speed multiplier (1.0 = full, ramps down to TARGET_SPEED)

    // Stop animation timing (accumulated frame time)
    this._stopElapsed = 0; // Accumulated stop animation time (ms)

    // Pause/resume needs no code: AppActivityContext sets ticker.speed = 0,
    // deltaMS becomes 0 and every accumulated timer freezes in place.

    // Get symbol data from spritesheet
    this.reelSymbolsData =
      spriteSheet?.data?.frames || spriteSheet?.textures || null;

    // Internal caches for performance optimization
    this._speedConfigCache = null;
    this._lastLockedSpeed = null;
    this._lastAutoPlaySpeed = null;
    // Cell height is fixed (native units), so the strip length is too.
    this._wrapThresholdCache = SPRITE_COUNT * this.symbolHeight;

    this._createSpinningSprites();
    this.spinningContainer.roundPixels = true;
  }

  // Picks a random symbol from available symbols (O(1) direct random)
  _pickRandomSymbolId() {
    if (!AVAILABLE_SYMBOL_IDS.length) {
      return 1; // fallback
    }
    return AVAILABLE_SYMBOL_IDS[
      Math.floor(Math.random() * AVAILABLE_SYMBOL_IDS.length)
    ];
  }

  // Calculates display value for coin symbols (coinValue * bet) with locale formatting
  _getCoinDisplayValue(symbolValue) {
    if (!symbolValue || symbolValue === null) {
      return "";
    }

    try {
      const coinValue = parseInt(symbolValue, 10);

      if (isNaN(coinValue) || coinValue <= 0) {
        return "";
      }

      // Use free rounds bet when active, otherwise use regular bet
      const state = store.getState();
      const freeRoundsActive = state?.freeRounds?.status === "active";
      const freeRoundsBet = state?.freeRounds?.betValue || null;
      const bet = freeRoundsActive && freeRoundsBet ? freeRoundsBet : this.bet;

      const displayValue = coinValue * bet;
      return formatToCustomLocale(displayValue);
    } catch (error) {
      return "";
    }
  }

  // Receives new reel data from API - used for landing symbols when reel stops
  updateReels(newReels) {
    if (newReels && Array.isArray(newReels)) {
      this.reels = newReels;
      this.hasPendingLandingSymbols = true;
    }
  }

  // Updates bet value. Coin texts pick it up when symbols wrap during the spin.
  updateBet(newBet) {
    this.bet = newBet || 1;
    this._warmCoinLabels();
  }

  // Refresh all visible coin value texts (used when bet changes during free rounds)
  _refreshVisibleCoinTexts() {
    if (!this.spinningSprites || this.spinningSprites.length === 0) return;

    // Refresh ALL spinning sprites (not just visibleSprites which may not be set yet)
    for (let i = 0; i < this.spinningSprites.length; i++) {
      const spriteData = this.spinningSprites[i];
      if (!spriteData || !spriteData.valueText || !spriteData.currentSymbolId)
        continue;

      const symbolInfo = symbolData[spriteData.currentSymbolId];
      if (!symbolInfo || !symbolInfo.value) continue;

      // Skip jackpot symbols — they don't display text values
      if (isJackpotSymbol(spriteData.currentSymbolId)) continue;

      // Recalculate coin display value with new bet
      const displayValue = this._getCoinDisplayValue(symbolInfo.value);
      if (displayValue && displayValue !== "") {
        spriteData.valueText.text = displayValue;
        if (spriteData.valueTextBlurred) {
          spriteData.valueTextBlurred.text = displayValue;
        }
      }
    }
  }

  // Updates texture for spinning sprites
  updateSymbolTexture(symbolId) {
    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo || this.spinningSprites.length === 0) return;

    const visibleSpriteIndex = this.spinningSprites.length - 1;
    this._setSpriteTexture(this.spinningSprites[visibleSpriteIndex], symbolId);
    this.symbolId = symbolId;
  }

  // Creates spinning sprites with wrapping
  _createSpinningSprites() {
    if (!this.spinningContainer) return;

    for (let i = 0; i < SPRITE_COUNT; i++) {
      const symbolContainer = new PIXI.Container();
      symbolContainer.x = this.reelXOffset;
      symbolContainer.y = i * this.symbolHeight;
      symbolContainer.roundPixels = true;

      const normalSprite = new PIXI.Sprite();
      normalSprite.anchor.set(0.5);
      normalSprite.name = "spinning_symbol_texture";
      normalSprite.alpha = 1;
      normalSprite.roundPixels = true;
      symbolContainer.addChild(normalSprite);

      const blurredSprite = new PIXI.Sprite();
      blurredSprite.anchor.set(0.5);
      blurredSprite.name = "spinning_symbol_texture_blurred";
      blurredSprite.alpha = 0;
      blurredSprite.roundPixels = true;
      symbolContainer.addChild(blurredSprite);

      let valueText = null;
      let valueTextBlurred = null;
      // The coin number is the Hold & Win label sprite (coinLabel: one cached
      // texture per value, never rounded). No blurred twin is created.
      if (FEATURES_CONFIG.SPIN.COIN_VALUES_ON_REELS && COIN_SYMBOL_IDS.length > 0) {
        valueText = createCoinLabel();
        valueText.text = ""; // the reel's checks read `.text`
        valueText.name = "symbol_value";
        valueText.zIndex = 1;
        symbolContainer.addChild(valueText);
      }

      this.spinningSprites.push({
        container: symbolContainer,
        normalSprite,
        blurredSprite,
        valueText,
        valueTextBlurred,
        currentSymbolId: null,
      });

      this.spinningContainer.addChild(symbolContainer);
    }

    this._updateSpinningSprites();
    this._hideBufferSprites();
    this.hasInitializedTextures = true;
    const visibleStart = VISIBLE_SYMBOL_START_INDEX;
    const visibleEnd = visibleStart + VISIBLE_SYMBOL_COUNT;
    this.visibleSprites = this.spinningSprites.slice(visibleStart, visibleEnd);
  }

  /**
   * Darken (or restore) this reel's spinning sprites with a tint: the scatter
   * anticipation's "reels waiting their turn" dim. Tints symbol and coin value
   * alike; no overlay.
   */
  setDimmed(dimmed, tint = 0x666666) {
    const value = dimmed ? tint : 0xffffff;
    for (const spriteData of this.spinningSprites ?? []) {
      if (spriteData.normalSprite) spriteData.normalSprite.tint = value;
      if (spriteData.blurredSprite) spriteData.blurredSprite.tint = value;
      if (spriteData.valueText) spriteData.valueText.tint = value;
      if (spriteData.valueTextBlurred) spriteData.valueTextBlurred.tint = value;
    }
  }

  // Symbol scale: SYMBOL_SCALE_OVERRIDES[symbolPay], falling back to DEFAULT.
  _calculateSymbolScale(symbolName, containerWidth, containerHeight, symbolId) {
    // Look up symbolPay for per-category scale
    const info = symbolId != null ? symbolData[symbolId] : null;
    const symbolPay = info?.symbolPay || '';
    const categoryScale = Settings.SYMBOL_SCALE_OVERRIDES?.[symbolPay]
      ?? Settings.SYMBOL_SCALE_OVERRIDES?.DEFAULT
      ?? 1;

    // The cell is native size, so only the category scale applies.
    return categoryScale;
  }

  /**
   * The scale a COMPOSITE is drawn at (the settled gamble scatter's open
   * window, a Golden cell). A composite is captured off its spine at native
   * size and the cell is native, so it is drawn 1:1. No category multiplier:
   * a composite has no symbolData id, and the id the cell still carries
   * describes the closed door, not what is drawn on it.
   */
  _applyCompositeScale(spriteData) {
    const scale = 1;
    if (spriteData.normalSprite) spriteData.normalSprite.scale.set(scale);
    if (spriteData.blurredSprite) spriteData.blurredSprite.scale.set(scale);
  }

  /**
   * Render priority for a static texture, matching the spine overlay's
   * hierarchy so both layers agree: M > D > C > jackpots > coin > scatter >
   * wild > pays. Matters most in turbo and super turbo, where landing spines
   * are skipped and the texture IS the visible symbol. A jackpot coin also
   * stacks by tier (JACKPOT_SKIN_RANK), as its spine does on the overlay.
   *
   * Guarded on change: writing zIndex flags the shared container sortDirty,
   * and this runs for every sprite as symbols recycle during a spin.
   */
  _applySpriteZIndex(spriteData, symbolId, jackpotSkin = null) {
    const container = spriteData?.container;
    if (!container || container.destroyed) return;
    spriteData._zBase =
      getSymbolZIndex(symbolId) +
      (symbolId === COIN_JACKPOT_SYMBOL_ID ? JACKPOT_SKIN_RANK[jackpotSkin] ?? 0 : 0);
    // A plain coin's value is set once its label is (_setCoinValueZ).
    spriteData._zValue = 0;
    this._applyRowZIndex(spriteData, this.spinningSprites.indexOf(spriteData));
  }

  /**
   * zIndex = the symbol's layer, then its coin value, row and reel
   * (cellZIndex): every reel's sprites share one sortable container and the
   * strip recycles its sprites, so equal symbols need the order spelled out.
   * `row` is the strip index (top = 0).
   */
  _applyRowZIndex(spriteData, row) {
    const container = spriteData?.container;
    if (!container || container.destroyed) return;
    const zIndex = cellZIndex(spriteData._zBase ?? 0, this.reelIndex, row, spriteData._zValue ?? 0);
    if (container.zIndex !== zIndex) container.zIndex = zIndex;
  }

  /** A plain coin stacks by the value it shows. */
  _setCoinValueZ(spriteData, value) {
    spriteData._zValue = value > 0 ? value : 0;
    this._applyRowZIndex(spriteData, this.spinningSprites.indexOf(spriteData));
  }

  /** Every sprite's row tie-break, after the strip is laid out on its grid. */
  _applyRowOrder() {
    this.spinningSprites.forEach((spriteData, row) => this._applyRowZIndex(spriteData, row));
  }

  /**
   * Resolve every symbol's normal/blurred textures ONCE, at construction.
   *
   * Not a texture pool (the spritesheet already is one). It only replaces the
   * per-wrap string-keyed lookup in `_setSpriteTexture` with one Map.get on a
   * number; several sprites wrap per frame in turbo. The sheets are never
   * reassigned, so the table never goes stale. `_setSpriteTexture` still
   * falls back to the string lookup for a symbol missing here.
   *
   * @returns {Map<number, {normal: PIXI.Texture, blurred: PIXI.Texture}>}
   */
  _buildTextureTable() {
    const table = new Map();
    const sheet = this.spritesheet?.textures;
    if (!sheet) return table;

    for (const key of Object.keys(symbolData)) {
      const textureName = symbolData[key]?.texture;
      if (!textureName) continue;
      const normal = sheet[`${textureName}.png`];
      if (!normal) continue;
      // No blurred sheet or frame: the normal texture is used for both.
      const blurred =
        this.spritesheetBlurred?.textures?.[`${textureName}_blurred.png`] ||
        normal;
      table.set(Number(key), { normal, blurred });
    }
    return table;
  }

  /**
   * The value the LANDING coin at `row` of this reel shows — the round's own
   * (round.coinValues, decided once in the adapter), so the picture and the
   * spine that plays on it agree. Null off the round: a cosmetic one is drawn.
   */
  _landingCoinValue(row) {
    return coinValueAt(store.getState().game?.currentRound, this.reelIndex, row);
  }

  /**
   * The skin the LANDING jackpot coin at `row` wears (round.jackpotCoinSkins,
   * PopiPlay RGS markJackpotCoins) — its picture shows the tier word like its
   * spine does. Null for any other cell.
   */
  _landingJackpotSkin(row) {
    return (
      store.getState().game?.currentRound?.jackpotCoinSkins?.[`${this.reelIndex}_${row}`] ??
      null
    );
  }

  /**
   * Pre-draw the coin labels this reel can show (the bet's cosmetic values)
   * once per bet and font size, so nothing is rasterised while the strip
   * moves. Cheap to call: keyed.
   */
  _warmCoinLabels() {
    if (!FEATURES_CONFIG.SPIN.COIN_VALUES_ON_REELS || !this.symbolHeight) return;
    const fontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
    const key = `${coinValueBet()}|${Math.round(fontSize)}`;
    if (this._coinWarmKey === key) return;
    this._coinWarmKey = key;
    warmCoinValueLabels(fontSize);
  }

  // Sets sprite textures (normal/blurred) and coin value text.
  _setSpriteTexture(spriteData, symbolId, coinValue = null, jackpotSkin = null) {
    // ── A composite override is cleared HERE, and nowhere else ──
    // A settled reveal's cell carries a COMPOSITE picture (see
    // setSymbolTextureOnly) that must ride out of view intact when the next
    // spin starts. This method runs when a sprite recycles to the far end of
    // the strip and is dealt a new symbol, so that is where it is dropped.
    //
    // The early return skips repainting an unchanged cell. "Unchanged" means
    // same id AND no override (else a recycled sprite dealt the same id keeps
    // the composite for ever), same jackpot skin, and, for a landing coin,
    // the same value (so picture and spine agree). A coin passing on the
    // strip (no value asked for) keeps what it shows.
    if (
      spriteData.currentSymbolId === symbolId &&
      !spriteData._staticOverride &&
      (spriteData._jackpotSkin ?? null) === jackpotSkin &&
      (coinValue == null || spriteData._coinValue === coinValue)
    ) {
      return;
    }
    spriteData._staticOverride = null;
    spriteData._jackpotSkin = null;

    // Ahead of every early return below, so a sprite that lands on an unknown
    // or textureless symbol still drops back to the default layer.
    this._applySpriteZIndex(spriteData, symbolId, jackpotSkin);

    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo) {
      spriteData.normalSprite.texture = PIXI.Texture.EMPTY;
      spriteData.normalSprite.alpha = 0.001;
      if (spriteData.blurredSprite) {
        spriteData.blurredSprite.texture = PIXI.Texture.EMPTY;
        spriteData.blurredSprite.alpha = 0;
      }
      if (spriteData.valueText) {
        spriteData.valueText.text = "";
        spriteData.valueText.alpha = 0;
      }
      spriteData.currentSymbolId = null;
      return;
    }

    spriteData.currentSymbolId = symbolId;
    const symbolName = symbolInfo.name;
    const textureName = symbolInfo.texture;
    const symbolValue = symbolInfo.value;

    // If texture is null or empty, set empty textures and skip processing
    if (!textureName || textureName === null) {
      spriteData.normalSprite.texture = PIXI.Texture.EMPTY;
      spriteData.normalSprite.alpha = 0.001;
      if (spriteData.blurredSprite) {
        spriteData.blurredSprite.texture = PIXI.Texture.EMPTY;
        spriteData.blurredSprite.alpha = 0;
      }
      if (spriteData.valueText) {
        spriteData.valueText.text = "";
        spriteData.valueText.alpha = 0;
      }
      spriteData.currentSymbolId = null;
      return;
    }

    if (this.spritesheet && textureName) {
      // Texture table first (see _buildTextureTable); the `||` chains are the
      // fallback for a symbol missing from it.
      const entry = this._textureTable?.get(symbolId);

      const texture =
        entry?.normal ||
        this.spritesheet.textures?.[`${textureName}.png`] ||
        PIXI.Texture.EMPTY;
      spriteData.normalSprite.texture = texture;
      spriteData.normalSprite.alpha = this.isBlurred ? 0 : 1;

      // A landing jackpot coin shows its tier word (MINI … GRAND). The sheet's
      // `cj` is the bare default skin, so use the skin's own extracted picture
      // (SpineTextureExtractor, cached).
      if (symbolId === COIN_JACKPOT_SYMBOL_ID && jackpotSkin) {
        const skinned = SpineTextureExtractor.getSkinTexture(symbolInfo.spine, jackpotSkin);
        if (skinned) {
          spriteData.normalSprite.texture = skinned;
          spriteData._jackpotSkin = jackpotSkin;
        }
      }

      if (this.spritesheetBlurred && spriteData.blurredSprite) {
        const blurredTexture =
          entry?.blurred ||
          this.spritesheetBlurred.textures?.[`${textureName}_blurred.png`] ||
          PIXI.Texture.EMPTY;
        spriteData.blurredSprite.texture = blurredTexture;
        spriteData.blurredSprite.alpha = this.isBlurred ? 1 : 0;
      }
    }

    if (symbolName) {
      const scale = this._calculateSymbolScale(
        symbolName,
        this.symbolWidth,
        this.symbolHeight,
        symbolId,
      );
      if (spriteData.normalSprite) {
        spriteData.normalSprite.scale.set(scale);
      }
      if (spriteData.blurredSprite) {
        // Blurred textures have same canvas size as normal (squash is baked into content).
        // Use same uniform scale so blurred sprites match normal sprite size.
        spriteData.blurredSprite.scale.set(scale);
      }
    }

    // ── Every plain coin shows a number ──
    // A landing coin its round value (`coinValue`), a coin passing on the
    // strip a cosmetic one. Never the jackpot coin (its tier word is its face).
    if (
      spriteData.valueText &&
      isCoinSymbol(symbolId) &&
      symbolId !== COIN_JACKPOT_SYMBOL_ID
    ) {
      const value = coinValue ?? cosmeticCoinValue();
      spriteData._coinValue = value; // what it shows — see the guard above
      this._setCoinValueZ(spriteData, value);
      const label = formatCoinValue(value);
      setCoinLabel(
        spriteData.valueText,
        label,
        this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER,
        this.symbolHeight * COIN_LABEL_MAX_WIDTH_RATIO, // the spine's ratio
      );
      spriteData.valueText.text = label; // read by the reel's own checks
      spriteData.valueText.y = 0;
      return;
    }

    // Other symbols with a `value` (not jackpots: they show their tier via
    // spine skins, not text) get a bet-scaled text.
    if (spriteData.valueText && symbolValue && !isJackpotSymbol(symbolId)) {
      const displayValue = this._getCoinDisplayValue(symbolValue);
      spriteData.valueText.text = displayValue;
      spriteData.valueText.alpha =
        displayValue !== "" ? (this.isBlurred ? 0 : 1) : 0;
      spriteData.valueText.y = -COIN_VALUE_Y_OFFSET;

      // Apply scaling logic if text has content
      if (displayValue !== "") {
        // Reset scale first
        spriteData.valueText.scale.set(1);

        // Force update to get accurate measurements
        spriteData.valueText.updateText();

        // Calculate available width (with padding)
        const coinWidth = this.symbolWidth;
        const paddingPercent = 0.12; // 12% padding on each side
        const maxTextWidth = coinWidth * (1 - paddingPercent * 2);

        const actualTextWidth = spriteData.valueText.width;

        // If text is too wide, scale down uniformly to fit (preserves aspect ratio)
        if (actualTextWidth > maxTextWidth) {
          const scale = maxTextWidth / actualTextWidth;
          spriteData.valueText.scale.set(scale);
        }
      }

      // Update blurred text with same value and scaling
      if (spriteData.valueTextBlurred) {
        spriteData.valueTextBlurred.text = displayValue;
        spriteData.valueTextBlurred.alpha =
          displayValue !== "" ? (this.isBlurred ? 1 : 0) : 0;
        spriteData.valueTextBlurred.y = -COIN_VALUE_Y_OFFSET;

        if (displayValue !== "") {
          spriteData.valueTextBlurred.scale.set(1);
          spriteData.valueTextBlurred.updateText();

          const coinWidth = this.symbolWidth;
          const paddingPercent = 0.1;
          const maxTextWidth = coinWidth * (1 - paddingPercent * 2);

          const actualTextWidth = spriteData.valueTextBlurred.width;

          // If text is too wide, scale down uniformly to fit (preserves aspect ratio)
          if (actualTextWidth > maxTextWidth) {
            const scale = maxTextWidth / actualTextWidth;
            spriteData.valueTextBlurred.scale.set(scale);
          }
        }
      }
    } else if (spriteData.valueText) {
      // Non-coin symbol: clear the number. The coin label is a sprite
      // (coinLabel), so clear it with an empty label; clearing only `.text`
      // and alpha would leave its `_label` and texture behind. A plain Text
      // is cleared directly.
      if (spriteData.valueText._label !== undefined) {
        setCoinLabel(spriteData.valueText, "");
      } else {
        spriteData.valueText.text = "";
        spriteData.valueText.alpha = 0;
      }
      if (spriteData.valueTextBlurred) {
        spriteData.valueTextBlurred.text = "";
        spriteData.valueTextBlurred.alpha = 0;
      }
    }
  }

  // Checks if any coin exists within MIN_COIN_SPACING positions below the top sprite (index 0).
  // After wrapping, index 0 is the newest sprite at the top. Indices 1, 2, etc. are below it.
  // This ensures coins are spaced apart in the strip, preventing visual stacking.
  _hasCoinWithinSpacing(spacing) {
    const limit = Math.min(spacing, this.spinningSprites.length);
    for (let i = 1; i < limit; i++) {
      const id = this.spinningSprites[i]?.currentSymbolId;
      if (id && isCoinSymbol(id)) return true;
    }
    return false;
  }

  // Generates random symbol for spin animation (cosmetic only — API controls landing).
  // Uses FEATURES_CONFIG.COIN_GENERATION to determine:
  //   - Which reels can show coins / royals
  //   - Probability per symbol position
  //   - Minimum spacing between coins
  _generateRandomSymbolId() {
    const coinConfig = FEATURES_CONFIG.COIN_GENERATION;

    // No coin generation configured — base symbols only
    if (!coinConfig || !coinConfig.MODE) {
      return this._pickRandomSymbolId();
    }

    // Royal symbol — only on configured reels
    if (
      ROYAL_SYMBOL_ID &&
      coinConfig.ROYAL_REELS?.includes(this.reelIndex) &&
      Math.random() < (coinConfig.ROYAL_PROBABILITY || 0)
    ) {
      return ROYAL_SYMBOL_ID;
    }

    // Coin symbols — only on configured reels, with spacing constraint
    if (
      COIN_SYMBOL_IDS.length > 0 &&
      coinConfig.COIN_REELS?.includes(this.reelIndex)
    ) {
      // Enforce minimum spacing between coins
      const spacing = coinConfig.MIN_COIN_SPACING || 1;
      if (spacing > 1 && this._hasCoinWithinSpacing(spacing)) {
        return this._pickRandomSymbolId();
      }

      if (Math.random() < (coinConfig.PROBABILITY || 0)) {
        return COIN_SYMBOL_IDS[
          Math.floor(Math.random() * COIN_SYMBOL_IDS.length)
        ];
      }
    }

    return this._pickRandomSymbolId();
  }

  // Initializes textures - visible sprites use API data, buffers use random symbols
  _updateSpinningSprites() {
    if (!this.spinningSprites.length) return;

    const shouldSkipSprite0 =
      this.pendingLandingSymbolId !== null && this._isLandingSpriteVisible();

    // Check if we have initial reels data from API
    const hasInitialReels =
      this.reels && this.reels.length >= VISIBLE_SYMBOL_COUNT;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      if (i === 0 && shouldSkipSprite0) continue;

      let symbolId;

      if (
        hasInitialReels &&
        i >= VISIBLE_SYMBOL_START_INDEX &&
        i < VISIBLE_SYMBOL_START_INDEX + VISIBLE_SYMBOL_COUNT
      ) {
        const reelIndex = i - VISIBLE_SYMBOL_START_INDEX;
        symbolId = this.reels[reelIndex];
        this._setSpriteTexture(
          this.spinningSprites[i],
          symbolId,
          this._landingCoinValue(reelIndex),
          this._landingJackpotSkin(reelIndex),
        );
        continue;
      } else {
        symbolId = this._generateRandomSymbolId();
      }

      this._setSpriteTexture(this.spinningSprites[i], symbolId);
    }
  }

  _resetSpritePositionsForSpin() {
    if (!this.spinningSprites.length) return;
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }
    this._applyRowOrder();
    this.reelYOffset = 0; // absolute placement — see the note in spin()
  }

  /**
   * Shift the whole reel vertically. Delta-based on purpose: each sprite keeps
   * its own relative position, so this is safe at rest or mid-wrap.
   */
  _setReelYOffset(nextOffset) {
    const delta = nextOffset - this.reelYOffset;
    if (delta === 0) return;
    for (let i = 0; i < this.spinningSprites.length; i++) {
      const sprite = this.spinningSprites[i];
      if (sprite?.container) sprite.container.y += delta;
    }
    this.reelYOffset = nextOffset;
    this._cullOffscreenSprites();
  }

  // Checks if landing zone sprites are visible - determines when to apply API landing symbols
  _isLandingSpriteVisible() {
    if (!this.spinningContainer || this.spinningSprites.length < SPRITE_COUNT) {
      return false;
    }

    const visibleTop = -this.symbolHeight / 2;
    const visibleBottom = this.symbolHeight / 2;
    const landingZoneStart = 1;
    const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;

    // Check if all landing zone sprites (1..N) are within visible bounds
    for (let i = landingZoneStart; i < landingZoneEnd; i++) {
      const sprite = this.spinningSprites[i];
      if (!sprite) return false;

      const absoluteY = sprite.container.y;
      if (absoluteY < visibleTop || absoluteY > visibleBottom) {
        return false;
      }
    }
    return true;
  }

  // Applies API landing symbols to landing zone - sprites drop into visible area on stop
  _setLandingSymbolOnVisibleSprites(symbolId = null) {
    const landingZoneStart = 1;
    const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;

    if (this.reels && this.reels.length >= VISIBLE_SYMBOL_COUNT) {
      for (let i = landingZoneStart; i < landingZoneEnd; i++) {
        if (this.spinningSprites.length > i) {
          const apiIndex = i - 1;
          const symbolIdForThisPosition = this.reels[apiIndex];

          if (
            symbolIdForThisPosition !== undefined &&
            symbolData[symbolIdForThisPosition]
          ) {
            this._setSpriteTexture(
              this.spinningSprites[i],
              symbolIdForThisPosition,
              this._landingCoinValue(apiIndex),
              this._landingJackpotSkin(apiIndex),
            );
          }
        }
      }
    } else if (symbolId !== null) {
      for (let i = landingZoneStart; i < landingZoneEnd; i++) {
        if (this.spinningSprites.length > i) {
          this._setSpriteTexture(this.spinningSprites[i], symbolId);
        }
      }
    }
  }

  // Start spin with wind-up animation
  spin() {
    if (this.isPlaying || this.isWindingUp) return;

    // Clear all previous animations and timeouts to prevent buildup
    this.clearAllTimeouts();

    this.lockedSpinSpeed = this.autoPlaySpeed;

    if (!this.spinningSprites.length) {
      this._createSpinningSprites();
      this.hasInitializedTextures = false;
    } else if (this.spinningSprites.length !== SPRITE_COUNT) {
      this._resetSpritePositionsForSpin();
    }

    if (this.spinningSprites.length > 0) {
      for (let i = 0; i < this.spinningSprites.length; i++) {
        this.spinningSprites[i].container.y = i * this.symbolHeight;
      }
      this._applyRowOrder();
      // Sprites were just placed absolutely, so clear the offset too, or the
      // delta-based _setReelYOffset would subtract it again (a freeze can
      // interrupt the wind-up and leave it non-zero).
      this.reelYOffset = 0;
    }

    this._showBufferSprites();
    this.stopTweenActive = false;
    this.stopAnimationProgress = 0;

    if (!this.hasInitializedTextures) {
      this._initializeSpinningTextures();
      this.hasInitializedTextures = true;
    }

    if (FEATURES_CONFIG.SPIN.WIND_UP_DURATION === 0) {
      this._startSpinAnimation();
      return;
    }

    this.isWindingUp = true;
    this._pendingStopKind = null;

    // roundPixels stays on: at the configured wind-up speed and depth
    // (FEATURES_CONFIG.SPIN.WIND_UP_*) no frame moves under a pixel. If a
    // slower wind-up stutters, see setRoundPixels().

    // Wind-up distance and duration match the bounce overshoot exactly.
    // easeOutBack overshoots by 4s³/(27(s+1)²) of total landing distance.
    // Bounce portion takes 2s/(3(s+1)) fraction of total stop time.
    const speedConfig = this._getSpeedConfig();
    const s = speedConfig.BOUNCE_OVERSHOOT;
    const overshootFraction = (4 * s * s * s) / (27 * (s + 1) * (s + 1));
    const windUpDistanceMultiplier = FEATURES_CONFIG.SPIN.WIND_UP_DISTANCE_MULTIPLIER ?? 1.0;
    const windUpDistance =
      -(overshootFraction * STOP_MOVE_DISTANCE_SYMBOLS * windUpDistanceMultiplier) *
      this.symbolHeight;

    // Calculate bounce duration from the stop timing to match wind-up timing
    const spinVelocity =
      (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;
    const totalStopDuration =
      (((s + 3) * STOP_MOVE_DISTANCE_SYMBOLS * this.symbolHeight * 1000) /
        spinVelocity) *
      speedConfig.STOP_SLOWDOWN_FACTOR;
    const bounceFraction = (2 * s) / (3 * (s + 1));
    const windUpSpeedFactor = FEATURES_CONFIG.SPIN.WIND_UP_SPEED_FACTOR ?? 1.0;
    const windUpDuration =
      Math.round(totalStopDuration * bounceFraction * windUpSpeedFactor) ||
      FEATURES_CONFIG.SPIN.WIND_UP_DURATION;

    const currentY = this.reelYOffset;
    const originalY = 0;

    if (Math.abs(currentY) > 0.001) {
      this._setReelYOffset(originalY);
    }

    // Wind-up: pull up, then release back to origin. Tweens a plain scalar
    // and pushes it onto the sprites each frame. The spin starts in the
    // complete callback, so anime.js and the Ticker loop never overlap.
    this._reelYTweenTarget.y = originalY;
    this.windUpAnimation = anime({
      targets: this._reelYTweenTarget,
      update: () => {
        this._setReelYOffset(this._reelYTweenTarget.y);
      },
      y: [
        {
          value: originalY + windUpDistance,
          duration: windUpDuration,
          easing: "easeOutCubic",
        },
        {
          value: originalY,
          duration: windUpDuration * 0.6,
          easing: "easeInCubic",
        },
      ],
      complete: () => {
        this._setReelYOffset(originalY);
        this.windUpAnimation = null;

        // Mark wind-up as done BEFORE dispatching — manual stop checks this flag
        this.isWindingUp = false;

        // Start spin animation FIRST — highest priority, no frame delay
        this._startSpinAnimation();

        // Replay a stop that arrived mid-wind-up. AFTER _startSpinAnimation,
        // because every stop bails unless isPlaying is set.
        const pendingStop = this._pendingStopKind;
        if (pendingStop) {
          this._pendingStopKind = null;
          if (pendingStop === "turbo") this.stopSpinTurbo();
          else if (pendingStop === "tension") this.stopSpinTension();
          else this.stopSpin();
        }

        // Defer Redux dispatch to AFTER paint — setTimeout(0) allows React 18 to batch
        // queueMicrotask runs before paint, causing 5 separate React re-renders per frame
        setTimeout(() => {
          this.dispatch(incrementWindupCount());
        }, 0);
      },
    });
  }

  // Start spin without wind-up (used for quick mode)
  spinNoWindUp() {
    if (this.isPlaying || this.isWindingUp) return;

    this.clearAllTimeouts();
    this.lockedSpinSpeed = this.autoPlaySpeed;

    if (!this.spinningSprites.length) {
      this._createSpinningSprites();
      this.hasInitializedTextures = false;
    } else if (this.spinningSprites.length !== SPRITE_COUNT) {
      this._resetSpritePositionsForSpin();
    }

    if (this.spinningSprites.length > 0) {
      for (let i = 0; i < this.spinningSprites.length; i++) {
        this.spinningSprites[i].container.y = i * this.symbolHeight;
      }
      this._applyRowOrder();
      // Sprites were just placed absolutely, so clear the offset too, or the
      // delta-based _setReelYOffset would subtract it again (a freeze can
      // interrupt the wind-up and leave it non-zero).
      this.reelYOffset = 0;
    }

    this._showBufferSprites();
    this.stopTweenActive = false;
    this.stopAnimationProgress = 0;

    if (!this.hasInitializedTextures) {
      this._initializeSpinningTextures();
      this.hasInitializedTextures = true;
    }

    // Jump straight to spinning — FIRST, before any dispatch
    this._startSpinAnimation();

    // Defer Redux dispatch to AFTER paint
    setTimeout(() => {
      this.dispatch(incrementWindupCount());
    }, 0);
  }

  // Start turbo spin (no wind-up, no animation, instant setup)
  spinTurbo() {
    if (this.isPlaying || this.isWindingUp) return;

    // Clear all previous animations and timeouts to prevent buildup
    this.clearAllTimeouts();

    this.lockedSpinSpeed = this.autoPlaySpeed;

    if (!this.spinningSprites.length) {
      this._createSpinningSprites();
      this.hasInitializedTextures = false;
    } else if (this.spinningSprites.length !== SPRITE_COUNT) {
      this._resetSpritePositionsForSpin();
    }

    this.stopTweenActive = false;
    this.stopAnimationProgress = 0;

    this.hasStopped = false;
    this.isStopping = false;
    this._wasManualStopped = false;
    this.isBlurred = false;
    this.speedMultiplier = 1.0;
    this.isTensionMode = false;
    this._spinElapsed = 0;

    this._switchToNormalTextures();

    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }
    this._applyRowOrder();

    this._showBufferSprites();
    this.reelYOffset = 0; // sprites were just placed absolutely by the loop above

    if (!this.hasInitializedTextures) {
      this._initializeSpinningTextures();
      this.hasInitializedTextures = true;
    }

    this.isPlaying = true;
  }

  // Stop turbo spin (instant drop with bounce)
  stopSpinTurbo() {
    if (this._deferStopDuringWindUp("turbo")) return;
    if (this.isStopping || this.hasStopped || !this.isPlaying) return;

    this.isStopping = true;

    this.isPlaying = false;
    this.stopTweenActive = true;

    // Stop the spin ticker — prevent competing rAF loops during drop animation
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    // Unblur all sprites so landing symbols are sharp during drop
    this._switchToNormalTextures();

    const landingZoneStart = 1;
    const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;

    for (let i = landingZoneStart; i < landingZoneEnd; i++) {
      const apiIndex = i - 1;
      const symbolId = this.reels[apiIndex];
      if (symbolId !== undefined) {
        this._setSpriteTexture(
          this.spinningSprites[i],
          symbolId,
          this._landingCoinValue(apiIndex),
          this._landingJackpotSkin(apiIndex),
        );
      }
    }

    const dropDistance = STOP_MOVE_DISTANCE_SYMBOLS * this.symbolHeight;
    const turboConfig = FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.TURBO;
    const dropDuration = turboConfig.DROP_DURATION || 300;
    const bounceOvershoot = turboConfig.BOUNCE_OVERSHOOT ?? 1.5; // Use nullish coalescing to allow 0

    let lastProgress = 0;
    this.stopAnimationProgress = 0;
    this.stopAnimationTotalDistance = dropDistance;

    this.activeStopAnimation = anime({
      targets: { progress: 0 },
      progress: dropDistance,
      duration: dropDuration,
      easing: function () {
        return function (t, overshoot = bounceOvershoot) {
          return --t * t * ((overshoot + 1) * t + overshoot) + 1;
        };
      },
      update: (anim) => {
        const currentProgress = anim.animations[0].currentValue;
        const delta = currentProgress - lastProgress;
        lastProgress = currentProgress;

        this.stopAnimationProgress = currentProgress / dropDistance;

        if (Math.abs(delta) < 0.0001) return;

        for (let i = 0; i < this.spinningSprites.length; i++) {
          this.spinningSprites[i].container.y += delta;
        }

        const wrapThreshold = this._wrapThresholdCache;
        for (let i = 0; i < this.spinningSprites.length; i++) {
          if (this.spinningSprites[i].container.y >= wrapThreshold) {
            this.spinningSprites[i].container.y -= wrapThreshold;
          }
        }

        this._cullOffscreenSprites();
      },
      complete: () => {
        this.activeStopAnimation = null;
        this.stopAnimationProgress = 0;

        // Rotate array: move last N sprites to front (single O(n) operation)
        const cut = this.spinningSprites.length - STOP_MOVE_DISTANCE_SYMBOLS;
        const tail = this.spinningSprites.splice(cut);
        this.spinningSprites.unshift(...tail);

        for (let i = 0; i < this.spinningSprites.length; i++) {
          this.spinningSprites[i].container.y = i * this.symbolHeight;
        }
        this._applyRowOrder();

        // Set critical state flags immediately
        this.reelYOffset = 0; // sprites were just placed absolutely by the loop above
        this.hasStopped = true;
        this.isStopping = false;
        this.stopTweenActive = false;
        this.lockedSpinSpeed = null;

        this._hideBufferSprites();

        // Fire landing animations first, before sounds and completion.
        if (this.onReelStop) {
          this.onReelStop(this.reelIndex, this.reels);
        }

        // One consolidated stop sound, played by the last reel, or by the last
        // non-tension reel when the reels after it are in tension.
        if (this.reelIndex === Settings.REEL_COUNT - 1 || this.isLastNonTensionReel) {
          const specialSound = this._getTurboModeSound();
          if (specialSound && specialSound !== SOUND_NAME.reel_stop) {
            // Scatter/mystery present: play only the special sound, skip reel_stop
            soundManager.play(specialSound);
          } else {
            // No special symbols — play a single reel_stop
            soundManager.play(SOUND_NAME.reel_stop);
          }
        }

        if (this.reelIndex === Settings.REEL_COUNT - 1) {
          this._completeSpin();
        }

        // Reset tension flag
        this.isLastNonTensionReel = false;
      },
    });
  }

  // Starts spin animation after wind-up completes
  _startSpinAnimation() {
    // Every label this spin can show, drawn BEFORE anything moves (keyed).
    this._warmCoinLabels();
    this.isPlaying = true;
    this.hasStopped = false;
    this.isStopping = false;
    this._wasManualStopped = false;
    this.isBlurred = false;
    this.stopTweenActive = false;
    this.speedMultiplier = 1.0;
    this.isTensionMode = false;
    this.isDecelerating = false;
    this.accelerationMultiplier = 0; // Start from 0, ramp up
    this.currentVelocity = 0;

    // Reset manual stop sound flag on first reel spin start
    if (this.reelIndex === 0) {
      ReelControllerV2._manualStopSoundPlayed = false;
    }

    // Reset tension deceleration for new spin
    this._tensionDecelerating = false;
    this._tensionDecelElapsed = 0;
    this._tensionSpeedMultiplier = 1.0;

    // Start reel_spin sound when first reel begins actual spinning (after wind-up)
    // Skip in super_turbo — spin is too fast for a looping sound to be audible
    if (this.reelIndex === 0 && !isSuperTurbo(this.autoPlaySpeed)) {
      soundManager.play(SOUND_NAME.reel_spin);
    }

    const speedConfig = this._getSpeedConfig();
    // Clamp speed to safe minimum: at 60fps each frame is ~16.7ms,
    // so speed must be >= 17ms/symbol to keep movement <= 1 symbolHeight/frame
    const safeSpeedMs = Math.max(speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT, 17);
    this.normalizedSpinSpeed =
      (this.symbolHeight / safeSpeedMs) * 1000;
    this._spinElapsed = 0;
    this.lastCalculatedPosition = 0;

    // No timer-based blur — velocity-based blur handles it in updateSymbolsAbsolute()

    this.tickerCallback = () => {
      if (!this.isPlaying && !this.stopTweenActive) {
        return;
      }
      this.updateSymbolsAbsolute();
    };

    PIXI.Ticker.shared.add(this.tickerCallback);
  }

  /**
   * Hold a stop that arrived before the wind-up finished; it is replayed in
   * the wind-up's complete callback.
   *
   * Stop requests are fire-and-forget (the TickerTimeout that raised one is
   * gone), so a dropped request means the reel spins for ever. And `isPlaying`
   * is false for the whole wind-up, so every stop's `!isPlaying` guard would
   * swallow it. Typical case: reel 0 in AUTO (zero stop delay) when the
   * wind-up outlasts the stop trigger. All three stop entry points use this.
   *
   * @returns {boolean} true when the caller should return immediately
   */
  _deferStopDuringWindUp(kind) {
    if (!this.isWindingUp) return false;
    // First request wins — a later one cannot downgrade an earlier stop.
    if (!this._pendingStopKind) this._pendingStopKind = kind;
    return true;
  }

  stopSpin() {
    if (this._deferStopDuringWindUp("normal")) return;

    if (this.isStopping || this.hasStopped || !this.isPlaying) return;

    this.isStopping = true;
    this._wasManualStopped = !!store.getState().spin.manualStop;

    this.speedMultiplier = 1.0;
    this.isTensionMode = false;
  }

  // Tension mode — gradually decelerates the reel to build anticipation.
  // The reel smoothly slows from full speed to TARGET_SPEED over a duration
  // derived from the tension spine cycle: cycleDuration × DECELERATION_RATIO.
  // Blur naturally fades out as velocity drops below BLUR_VELOCITY_THRESHOLD.
  spinTension() {
    if (!this.isPlaying || this.isStopping || this.hasStopped) return;
    this.isTensionMode = true;

    const tensionDecel = FEATURES_CONFIG.TENSION.REEL_DECELERATION;
    if (tensionDecel?.ENABLED) {
      // Derive deceleration duration from spine data × ratio (no hardcoded ms)
      const cycleDuration = SpineAnimationData.getTensionCycleDuration()
        || FEATURES_CONFIG.TENSION.DURATION; // Fallback to config if spine not cached yet
      this._tensionDecelerationDuration = cycleDuration * (tensionDecel.DECELERATION_RATIO ?? 0.45);
      this._tensionDecelerating = true;
      this._tensionDecelElapsed = 0;
      this._tensionSpeedMultiplier = 1.0; // Start at current speed, ramps down gradually
    }
  }

  // Stops tension mode spin — just triggers normal stop
  // Marks reel as tension so _finalizeLanding can play per-reel sounds (not consolidated turbo sound)
  stopSpinTension() {
    if (this._deferStopDuringWindUp("tension")) return;
    if (this.isStopping || this.hasStopped || !this.isPlaying) return;
    this.isStopping = true;

    this.isTensionMode = true;
  }

  // Freeze spin instantly - stops all animations without landing
  // Used for error popups where reels should freeze at current position
  freezeSpin() {
    // Stop ticker immediately
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    // Stop wind-up if active
    if (this.windUpAnimation) {
      this.windUpAnimation.pause();
      anime.remove(this._reelYTweenTarget); // Force-eject from anime.js internal tracker
      this.windUpAnimation = null;
      this.isWindingUp = false;
      this._pendingStopKind = null;
    }

    // Clear ticker-based stop animation params
    this._stopElapsed = 0;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;
    this.activeStopAnimation = null;

    // Set state flags
    this.isPlaying = false;
    this.hasStopped = true;
    this.isStopping = false;
    this.stopTweenActive = false;
    this.isTensionMode = false;
    this.speedMultiplier = 1.0;
    this._spinElapsed = 0;
    this.lockedSpinSpeed = null;
    this.accelerationMultiplier = 1.0;
    this.currentVelocity = 0;
    this.isDecelerating = false;

    // Reset tension deceleration state
    this._tensionDecelerating = false;
    this._tensionDecelElapsed = 0;
    this._tensionSpeedMultiplier = 1.0;

    // Clear all pending timeouts
    this.clearAllTimeouts();

    // DO NOT call onReelStop - no landing animations
    // DO NOT dispatch setSpinComplete - popup handles flow
    // DO NOT switch textures - keep current blur state
  }

  // Pause/resume for tab switching: no-ops, kept for callers.
  // AppActivityContext sets PIXI.Ticker.shared.speed = 0 on tab hide, so
  // deltaMS = 0 and every accumulated timer freezes on its own.
  pauseSpin() {}
  resumeSpin() {}

  // Restore reel to idle state with specific symbols
  // Used after error recovery to show previous round's symbols properly
  // @param {Array} symbolIds - Array of symbol IDs to restore
  // @param {number|null} betValue - Optional bet value to use for coin texts (for error recovery)
  restoreToIdleState(symbolIds, betValue = null) {
    // Clear all animations and timeouts
    this.clearAllTimeouts();

    // Stop ticker if active
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    // Stop any active animations
    if (this.windUpAnimation) {
      this.windUpAnimation.pause();
      anime.remove(this._reelYTweenTarget); // Force-eject from anime.js internal tracker
      this.windUpAnimation = null;
      this.isWindingUp = false;
      this._pendingStopKind = null;
    }

    // Clear ticker-based stop animation params
    this._stopElapsed = 0;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;
    this.activeStopAnimation = null;

    // Reset state flags
    this.isPlaying = false;
    this.hasStopped = true;
    this.isStopping = false;
    this.stopTweenActive = false;
    this.isTensionMode = false;
    this.isBlurred = false;
    this.speedMultiplier = 1.0;
    this._spinElapsed = 0;
    this.lockedSpinSpeed = null;
    this.accelerationMultiplier = 1.0;
    this.currentVelocity = 0;
    this.isDecelerating = false;

    // Reset tension deceleration state
    this._tensionDecelerating = false;
    this._tensionDecelElapsed = 0;
    this._tensionSpeedMultiplier = 1.0;

    // Switch to normal (non-blurred) textures
    this._switchToNormalTextures();

    // Ensure sprites exist
    if (!this.spinningSprites.length) {
      this._createSpinningSprites();
    }

    // Reset container position to default
    this.reelYOffset = 0; // the loop below places every sprite absolutely

    // Position all sprites to their default positions
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }
    this._applyRowOrder();

    // Paint the provided symbols on the visible sprites
    // (VISIBLE_SYMBOL_START_INDEX .. + VISIBLE_SYMBOL_COUNT)
    if (symbolIds && Array.isArray(symbolIds)) {
      // Store original bet and temporarily use historical bet if provided
      const originalBet = this.bet;
      if (betValue !== null && betValue !== undefined) {
        this.bet = betValue;
      }

      for (let i = 0; i < VISIBLE_SYMBOL_COUNT && i < symbolIds.length; i++) {
        const spriteIndex = VISIBLE_SYMBOL_START_INDEX + i;
        const symbolId = symbolIds[i];
        if (symbolId !== undefined && this.spinningSprites[spriteIndex]) {
          this._setSpriteTexture(
            this.spinningSprites[spriteIndex],
            symbolId,
            this._landingCoinValue(i),
            this._landingJackpotSkin(i),
          );
        }
      }

      // Fill other sprites with random symbols for smooth next spin
      for (let i = 0; i < this.spinningSprites.length; i++) {
        if (
          i < VISIBLE_SYMBOL_START_INDEX ||
          i >= VISIBLE_SYMBOL_START_INDEX + VISIBLE_SYMBOL_COUNT
        ) {
          const randomSymbolId = this._generateRandomSymbolId();
          this._setSpriteTexture(this.spinningSprites[i], randomSymbolId);
        }
      }

      // Update reels data for next spin
      this.reels = symbolIds;
      this.hasPendingLandingSymbols = false;

      // Do NOT call onReelStop here: it runs full landing animations. The
      // caller (ErrorRecoveryOverlay) triggers idle-only animations via
      // triggerInitialIdleAnimations() once all reels are restored.

      // Restore original bet
      if (betValue !== null && betValue !== undefined) {
        this.bet = originalBet;
      }
    }

    this._hideBufferSprites();
  }

  // Toggle roundPixels on all sprites
  // Disable during tension zoom to prevent jitter from fractional container scaling
  // Re-enable after zoom completes for crisp 1:1 rendering
  setRoundPixels(enabled) {
    this.spinningContainer.roundPixels = enabled;
    for (let i = 0; i < this.spinningSprites.length; i++) {
      const sprite = this.spinningSprites[i];
      sprite.container.roundPixels = enabled;
      sprite.normalSprite.roundPixels = enabled;
      sprite.blurredSprite.roundPixels = enabled;
      if (sprite.valueText) sprite.valueText.roundPixels = enabled;
      if (sprite.valueTextBlurred) sprite.valueTextBlurred.roundPixels = enabled;
    }
  }

  // Main animation loop
  updateSymbolsAbsolute() {
    if (this.hasStopped) {
      return;
    }

    // --- Ticker-based stop animation (same ticker as the spin: seamless handoff) ---
    if (this.stopTweenActive && this._stopDuration > 0) {
      this._stopElapsed += PIXI.Ticker.shared.deltaMS;
      const elapsed = this._stopElapsed;
      const t = Math.min(elapsed / this._stopDuration, 1.0);

      // BackOut easing: f(t) = (t-1)² × ((s+1)(t-1) + s) + 1
      const s = this._stopOvershoot;
      const t1 = t - 1;
      const easedT = t1 * t1 * ((s + 1) * t1 + s) + 1;
      const easedPosition = this._stopDistance * easedT;
      const delta = easedPosition - this._stopLastEasedPos;
      this._stopLastEasedPos = easedPosition;

      this.stopAnimationProgress = easedPosition / this._stopDistance;

      // Update blur during stop (velocity decreases as easing decelerates)
      // BackOut derivative: f'(t) = 3(s+1)(t-1)² + 2s(t-1)
      const easingVelocity = 3 * (s + 1) * t1 * t1 + 2 * s * t1;
      this.currentVelocity = Math.abs(easingVelocity) * 0.3;
      this._updateBlurFromVelocity();

      if (Math.abs(delta) > 0.0001) {
        for (let i = 0; i < this.spinningSprites.length; i++) {
          this.spinningSprites[i].container.y += delta;
        }

        const wrapThreshold = this._wrapThresholdCache;
        for (let i = 0; i < this.spinningSprites.length; i++) {
          if (this.spinningSprites[i].container.y >= wrapThreshold) {
            this.spinningSprites[i].container.y -= wrapThreshold;
          }
        }

        this._cullOffscreenSprites();
      }

      // Detect overshoot peak (trough) — the exact frame where symbols reach their lowest point
      // BackOut peak: t_peak = 1 - 2s / (3(s+1)), only when s > 0 (has bounce)
      if (!this._landingPeakFired && this._stopOvershoot > 0) {
        const sPeak = this._stopOvershoot;
        const tPeakMath = 1 - (2 * sPeak) / (3 * (sPeak + 1));
        // LANDING_PEAK_OFFSET: 1.0 = exact trough, <1.0 = fires earlier
        const peakOffset = this._getSpeedConfig()?.LANDING_PEAK_OFFSET ?? 1.0;
        const tPeak = tPeakMath * peakOffset;
        if (t >= tPeak) {
          this._landingPeakFired = true;
          // The landing sound plays on the impact frame (see _playLandingSound).
          // Skipped in turbo/manual stop: _finalizeLanding plays one stop
          // sound for the board instead.
          const skipPeakSound = this._wasManualStopped || isTurboLike(this.autoPlaySpeed);
          if (!skipPeakSound) {
            this._playLandingSound();
          }
          if (this.onLandingPeak) {
            this.onLandingPeak(this.reelIndex);
          }
        }
      }

      if (t >= 1.0) {
        this._finalizeLanding();
      }
      return;
    }

    // Accumulate elapsed time from ticker (freezes when tab hidden)
    this._spinElapsed += PIXI.Ticker.shared.deltaMS;
    const elapsedTime = this._spinElapsed / 1000;
    const currentExpectedPosition = elapsedTime * this.normalizedSpinSpeed;

    let movement = currentExpectedPosition - this.lastCalculatedPosition;
    this.lastCalculatedPosition = currentExpectedPosition; // Always track at full speed

    // --- Tension deceleration (smooth slowdown for anticipation) ---
    // Scales the per-frame visual movement while keeping position tracking at full speed.
    // This way the gap doesn't grow to compensate — the reel genuinely scrolls slower.
    if (this._tensionDecelerating) {
      const tensionDecel = FEATURES_CONFIG.TENSION.REEL_DECELERATION;
      this._tensionDecelElapsed += PIXI.Ticker.shared.deltaMS;
      const tensionElapsed = this._tensionDecelElapsed;
      const tDecel = Math.min(tensionElapsed / this._tensionDecelerationDuration, 1.0);
      // Ease-out cubic: starts slowing immediately, arrives at target speed gradually.
      // Produces a visible, dramatic deceleration from the very first frame.
      const t1 = 1 - tDecel;
      const easedDecel = 1 - (t1 * t1 * t1);
      this._tensionSpeedMultiplier = 1.0 - (1.0 - tensionDecel.TARGET_SPEED) * easedDecel;

      // Scale visual movement only — position tracker stays at full speed
      movement *= this._tensionSpeedMultiplier;
    }

    // --- Acceleration ramp-up (ease-out: fast start, smooth arrival at full speed) ---
    const speedConfig = this._getSpeedConfig();
    // Absolute ACCELERATION_DURATION if set, else ratio-based (ACCEL_SPEED_RATIO × SPEED_MS)
    const accelDuration = speedConfig.ACCELERATION_DURATION
      ?? (speedConfig.ACCEL_SPEED_RATIO
        ? speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT * speedConfig.ACCEL_SPEED_RATIO
        : 0);
    if (accelDuration > 0 && this.accelerationMultiplier < 1.0) {
      const elapsedMs = this._spinElapsed;
      const t = Math.min(elapsedMs / accelDuration, 1.0);
      // Ease-out cubic: starts fast, smoothly reaches 1.0
      this.accelerationMultiplier = 1 - (1 - t) * (1 - t) * (1 - t);
    } else {
      this.accelerationMultiplier = 1.0;
    }

    // Apply acceleration multiplier
    movement *= this.accelerationMultiplier;

    // --- Blur: velocity-based sharp/blurred swap ---
    // During tension deceleration, velocity tracks the tension speed multiplier
    // → blur naturally fades out as speed drops below BLUR_VELOCITY_THRESHOLD
    this.currentVelocity = this._tensionDecelerating
      ? this._tensionSpeedMultiplier
      : this.accelerationMultiplier;
    this._updateBlurFromVelocity();

    const wrapThreshold = this._wrapThresholdCache;
    const shouldUpdateTexture = !this.isStopping && !this.hasStopped;

    if (
      this.pendingLandingSymbolId !== null &&
      !this._isLandingSpriteVisible()
    ) {
      this._setLandingSymbolOnVisibleSprites(this.pendingLandingSymbolId);
      this.pendingLandingSymbolId = null;
    }

    let shouldExecuteStop = false;

    // Phase 1: Apply movement to ALL sprites uniformly (no break-and-snap)
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y += movement;
    }

    this._cullOffscreenSprites();

    // Phase 2: Check for wraps AFTER all sprites have moved
    // While loop handles multiple wraps per frame at high speeds (safety for extreme SPEED_MS values)
    let wrapsThisFrame = 0;
    const lastIndex = this.spinningSprites.length - 1;
    while (
      this.spinningSprites[lastIndex].container.y >= wrapThreshold &&
      wrapsThisFrame < SPRITE_COUNT // Safety cap: can never need more wraps than sprites
    ) {
      wrapsThisFrame++;

      this.moveLastSymbolOnTop();

      if (
        this.pendingLandingSymbolId !== null &&
        !this._isLandingSpriteVisible()
      ) {
        this._setLandingSymbolOnVisibleSprites(this.pendingLandingSymbolId);
        this.pendingLandingSymbolId = null;
      }

      if (shouldUpdateTexture) {
        // Update texture on the sprite that just wrapped to top (now index 0)
        this.updateSymbolTextureRandomly(this.spinningSprites[0]);
      }

      if (this.isStopping && !this.hasStopped && !this.stopTweenActive) {
        shouldExecuteStop = true;
      }
    }

    if (shouldExecuteStop) {
      this._calculateAndExecuteStop();
    }
  }

  // Wraps last sprite to top — single-sprite teleport (no grid-snap on other sprites)
  moveLastSymbolOnTop() {
    const movedSymbol = this.spinningSprites.pop();
    this.spinningSprites.unshift(movedSymbol);

    // Teleport only the wrapped sprite to the top position.
    // Other sprites keep their exact Y positions — no grid-snap stutter.
    // The total strip height is the distance from top to wrap threshold.
    const totalStripHeight = SPRITE_COUNT * this.symbolHeight;
    movedSymbol.container.y -= totalStripHeight;
  }

  // Calculates current drift from expected positions
  _calculateCurrentDrift() {
    if (!this.spinningSprites.length) return 0;

    const visibleStartIndex = VISIBLE_SYMBOL_START_INDEX;
    const visibleEndIndex = visibleStartIndex + VISIBLE_SYMBOL_COUNT;

    let totalDrift = 0;
    let count = 0;

    for (
      let i = visibleStartIndex;
      i < visibleEndIndex && i < this.spinningSprites.length;
      i++
    ) {
      const expectedY = i * this.symbolHeight;
      const actualY = this.spinningSprites[i].container.y;
      const drift = actualY - expectedY;
      totalDrift += drift;
      count++;
    }

    const averageDrift = count > 0 ? totalDrift / count : 0;
    return averageDrift;
  }

  // Updates sprite with random symbol
  updateSymbolTextureRandomly(spriteData) {
    const randomSymbolId = this._generateRandomSymbolId();
    this._setSpriteTexture(spriteData, randomSymbolId);
  }

  // Initializes textures for spinning sprites
  _initializeSpinningTextures() {
    // Rebuild the texture table if the sheet was not ready at construction.
    // An empty table still works (string-lookup fallback) but is slower.
    if (this._textureTable?.size === 0 && this.spritesheet?.textures) {
      this._textureTable = this._buildTextureTable();
    }

    this.spinningSprites.forEach((spriteData, index) => {
      const randomSymbolId = this._generateRandomSymbolId();
      this._setSpriteTexture(spriteData, randomSymbolId);
    });
    this.hasInitializedTextures = true;
  }

  /**
   * The ONE sound this reel makes as it lands, played on the impact frame
   * (overshoot peak), not the settle.
   *
   * A reel carrying a scatter or a mystery plays that INSTEAD of the
   * `reel_stop` thud, never both. The special uses playOncePerFrame because
   * turbo and manual stop land every reel together; it is keyed by name, so a
   * scatter on one reel and a mystery on another both still sound.
   *
   * @param {object}  [opts]
   * @param {boolean} [opts.thud] False where the board's single stop sound
   *   is already played (manual stop), so only the announcement is added.
   */
  _playLandingSound({ thud = true } = {}) {
    const special = this._getReelStopSound();
    if (special && special !== SOUND_NAME.reel_stop) {
      soundManager.playOncePerFrame(special);
      return;
    }
    if (thud) soundManager.play(SOUND_NAME.reel_stop);
  }

  /**
   * The symbols that will stand in the VISIBLE window once this reel settles:
   * the landing data, not the sprites.
   *
   * The landing sound fires at the overshoot peak, when the strip is past its
   * final position and the visible sprite slots still hold symbols about to
   * slide back out of view. `this.reels` (first VISIBLE_SYMBOL_COUNT entries =
   * the visible rows, as in _setLandingSymbolOnVisibleSprites) does not move.
   *
   * Falls back to the settled sprites when there is no landing data (idle
   * board, or a reel never given API symbols).
   *
   * @returns {number[]} visible symbol ids, may be empty
   */
  _visibleLandedSymbolIds() {
    const ids = [];
    if (Array.isArray(this.reels) && this.reels.length >= VISIBLE_SYMBOL_COUNT) {
      for (let i = 0; i < VISIBLE_SYMBOL_COUNT; i++) ids.push(this.reels[i]);
      return ids;
    }
    for (let i = 0; i < VISIBLE_SYMBOL_COUNT; i++) {
      ids.push(this.spinningSprites[VISIBLE_SYMBOL_START_INDEX + i]?.currentSymbolId);
    }
    return ids;
  }

  /**
   * Does a MYSTERY symbol stand on this reel? Asked directly because
   * _getReelStopSound returns one sound by priority, and a scatter would hide
   * the mystery.
   *
   * @returns {boolean}
   */
  _hasLandedMystery() {
    for (const symbolId of this._visibleLandedSymbolIds()) {
      if (symbolId === undefined || symbolId === null) continue;
      if (symbolData[symbolId]?.symbolPay === "mystery") return true;
    }
    return false;
  }

  _getReelStopSound() {
    let hasScatter = false;
    let hasMystery = false;
    let hasCoin = false;
    let hasWild = false;
    // The jackpot coins' tiers on this reel (batch 3, 2026-10-22).
    const jackpotSkins = [];

    // The board as it will settle (see _visibleLandedSymbolIds).
    // for (const symbolId of this._visibleLandedSymbolIds()) {
    const landed = this._visibleLandedSymbolIds();
    for (let row = 0; row < landed.length; row++) {
      const symbolId = landed[row];
      if (symbolId === undefined || symbolId === null) continue;

      const symInfo = symbolData[symbolId];
      if (!symInfo) continue;

      if (symInfo.symbolPay === "scatter") {
        hasScatter = true;
      } else if (symInfo.symbolPay === "mystery") {
        hasMystery = true;
      } else if (symInfo.symbolPay === "coin") {
        hasCoin = true;
        if (symbolId === COIN_JACKPOT_SYMBOL_ID) jackpotSkins.push(this._landingJackpotSkin(row));
      } else if (symInfo.symbolPay === "wild") {
        hasWild = true;
      }
    }

    if (hasScatter) return SOUND_NAME.scatter_land;
    if (hasMystery) return SOUND_NAME.mystery_land;
    // Coin and wild have their own landing sounds (2026-10-19): the coin's
    // fireball landing and the wild's. A scatter still takes the reel.
    // if (hasCoin) return SOUND_NAME.coin_land;
    // A jackpot coin: its tier's sound instead (the highest on the reel).
    if (hasCoin) return jackpotLandSound(jackpotSkins) ?? SOUND_NAME.coin_land;
    if (hasWild) return SOUND_NAME.wild_land;
    // (Until 2026-10-19 wild and coin had no landing sound of their own and
    // got the thud.) Everything else still does.
    return SOUND_NAME.reel_stop;
  }

  // Determines turbo mode stop sound by checking all reels - used on last reel
  _getTurboModeSound() {
    const state = store.getState();
    if (
      !state ||
      !state.game ||
      !state.game.currentRound ||
      !state.game.currentRound.reels
    ) {
      return this._getReelStopSound();
    }

    const allReels = state.game.currentRound.reels;
    let hasScatter = false;
    let hasMystery = false;
    let hasCoin = false;
    let hasWild = false;
    // The jackpot coins' tiers on the board (batch 3, 2026-10-22).
    const jackpotSkins = [];
    const skinsByCell = state.game.currentRound.jackpotCoinSkins || {};

    for (let reelIndex = 0; reelIndex < allReels.length; reelIndex++) {
      const reel = allReels[reelIndex];
      if (!reel) continue;

      for (let i = 0; i < VISIBLE_SYMBOL_COUNT; i++) {
        const symbolId = reel[i];
        if (symbolId === undefined) continue;

        const symInfo = symbolData[symbolId];
        if (!symInfo) continue;

        if (symInfo.symbolPay === "scatter") {
          hasScatter = true;
        } else if (symInfo.symbolPay === "mystery") {
          hasMystery = true;
        } else if (symInfo.symbolPay === "coin") {
          hasCoin = true;
          if (symbolId === COIN_JACKPOT_SYMBOL_ID) jackpotSkins.push(skinsByCell[`${reelIndex}_${i}`]);
        } else if (symInfo.symbolPay === "wild") {
          hasWild = true;
        }
      }
    }

    if (hasScatter) return SOUND_NAME.scatter_land;
    if (hasMystery) return SOUND_NAME.mystery_land;
    // Coin and wild have their own landing sounds (2026-10-19): the coin's
    // fireball landing and the wild's. A scatter still takes the reel.
    // if (hasCoin) return SOUND_NAME.coin_land;
    // A jackpot coin: its tier's sound instead (the highest on the board).
    if (hasCoin) return jackpotLandSound(jackpotSkins) ?? SOUND_NAME.coin_land;
    if (hasWild) return SOUND_NAME.wild_land;
    // (Until 2026-10-19 wild and coin had no landing sound of their own and
    // got the thud.) Everything else still does.
    return SOUND_NAME.reel_stop;
  }

  // Sets win state for this spin - affects sound playback
  setHasWinThisSpin(hasWin) {
    this.hasWinThisSpin = !!hasWin;
  }

  // Sets Hold & Win trigger state for this spin - affects spin complete delay
  setIsHoldAndWinTrigger(isTrigger) {
    this.isHoldAndWinTrigger = !!isTrigger;
  }

  // Gets speed configuration based on locked or current autoPlaySpeed
  // Cached for performance - invalidated when speed changes
  _getSpeedConfig() {
    const effectiveSpeed = this.lockedSpinSpeed || this.autoPlaySpeed;

    // Check cache validity
    if (
      this._speedConfigCache !== null &&
      this._lastLockedSpeed === this.lockedSpinSpeed &&
      this._lastAutoPlaySpeed === this.autoPlaySpeed
    ) {
      return this._speedConfigCache;
    }

    const config =
      FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS[effectiveSpeed?.toUpperCase()] ||
      FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.AUTO;

    // Cache result
    this._speedConfigCache = config;
    this._lastLockedSpeed = this.lockedSpinSpeed;
    this._lastAutoPlaySpeed = this.autoPlaySpeed;

    return config;
  }

  // Unblur all sprites - instant transition
  _switchToNormalTextures() {
    if (!this.spritesheetBlurred) {
      return Promise.resolve();
    }

    this.spinningSprites.forEach((sprite) => {
      if (!sprite || !sprite.blurredSprite) return;
      sprite.normalSprite.alpha = 1;
      sprite.blurredSprite.alpha = 0;

      // Switch text back to normal
      if (sprite.valueText && sprite.valueText.text !== "") {
        sprite.valueText.alpha = 1;
      }
      if (sprite.valueTextBlurred) {
        sprite.valueTextBlurred.alpha = 0;
      }
    });

    this.isBlurred = false;

    return Promise.resolve();
  }

  // Selectively unblurs landing symbols during stop animation
  _switchToNormalTexturesSelective() {
    if (!this.spritesheetBlurred) return;

    const landingZoneStart = 1;
    const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;
    const bufferAbove = landingZoneStart - 1;
    const bufferBelow = landingZoneEnd;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      const spriteData = this.spinningSprites[i];
      if (!spriteData || !spriteData.blurredSprite) continue;

      const isLandingZone = i >= landingZoneStart && i < landingZoneEnd;
      const isBufferAbove = i === bufferAbove;
      const isBufferBelow = i === bufferBelow;

      if (isLandingZone || isBufferAbove || isBufferBelow) {
        spriteData.normalSprite.alpha = 1;
        spriteData.blurredSprite.alpha = 0;

        // Switch text to normal for landing symbols
        if (spriteData.valueText && spriteData.valueText.text !== "") {
          spriteData.valueText.alpha = 1;
        }
        if (spriteData.valueTextBlurred) {
          spriteData.valueTextBlurred.alpha = 0;
        }
      }
    }

    this.isBlurred = false;
  }

  // Velocity-based blur: shows the normal or the blurred sprite. Below
  // BLUR_VELOCITY_THRESHOLD always sharp; above it, blurred past the ramp's midpoint.
  _updateBlurFromVelocity() {
    if (!this.spritesheetBlurred) return;

    const speedConfig = this._getSpeedConfig();
    const threshold = speedConfig.BLUR_VELOCITY_THRESHOLD || 0.6;
    const velocity = this.currentVelocity;

    // Calculate blur amount: 0 = fully sharp, 1 = fully blurred
    // Ramp from threshold to 1.0 (full speed)
    let blurAmount;
    if (velocity < threshold) {
      blurAmount = 0;
    } else {
      // Normalized 0-1 within the blur range, squared for natural feel
      const t = (velocity - threshold) / (1.0 - threshold);
      blurAmount = Math.min(t * t, 1.0);
    }

    // Update isBlurred state for cleanup/state tracking
    this.isBlurred = blurAmount > 0.5;

    // A SWAP, not a crossfade: only one of the two sprites is drawn (alpha 0
    // lets PIXI skip the other), which halves fill and texture count while
    // spinning. The blur ramp spans about two frames, so a crossfade would not
    // be visible anyway; the swap happens at the ramp's midpoint.
    const showBlurred = blurAmount > 0.5;
    const normalAlpha = showBlurred ? 0 : 1;
    const blurredAlpha = showBlurred ? 1 : 0;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      const spriteData = this.spinningSprites[i];
      if (!spriteData || !spriteData.blurredSprite) continue;

      // During stop animation, skip landing zone + buffer sprites (already unblurred)
      if (this.stopTweenActive) {
        const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;
        if (i >= 0 && i <= landingZoneEnd) continue;
      }

      spriteData.normalSprite.alpha = normalAlpha;
      spriteData.blurredSprite.alpha = blurredAlpha;

      // Coin value text follows the same swap
      if (spriteData.valueText && spriteData.valueText.text !== "") {
        spriteData.valueText.alpha = normalAlpha;
      }
      if (
        spriteData.valueTextBlurred &&
        spriteData.valueTextBlurred.text !== ""
      ) {
        spriteData.valueTextBlurred.alpha = blurredAlpha;
      }
    }
  }

  // Ticker-based landing animation: runs in the same PIXI ticker as the spin,
  // so there is no frame gap between spin and stop. BackOut easing with a
  // configurable overshoot gives the bounce.
  _calculateAndExecuteStop() {
    if (this.hasStopped || this.stopTweenActive) return;
    this.stopTweenActive = true;

    // DON'T remove the ticker — the stop animation runs inside it

    const hasApiData = this.reels && this.reels.length >= VISIBLE_SYMBOL_COUNT;

    if (hasApiData) {
      if (
        this.spinningSprites.length >= SPRITE_COUNT &&
        !this._isLandingSpriteVisible()
      ) {
        this._setLandingSymbolOnVisibleSprites();
      }
    } else if (this.pendingLandingSymbolId !== null) {
      if (
        this.spinningSprites.length >= SPRITE_COUNT &&
        !this._isLandingSpriteVisible()
      ) {
        this._setLandingSymbolOnVisibleSprites(this.pendingLandingSymbolId);
        this.symbolId = this.pendingLandingSymbolId;
        this.pendingLandingSymbolId = null;
      }
    }

    const currentDrift = this._calculateCurrentDrift();
    const baseLandingDistance = STOP_MOVE_DISTANCE_SYMBOLS * this.symbolHeight;
    const adjustedLandingDistance = baseLandingDistance - currentDrift;

    const speedConfig = this._getSpeedConfig();
    const overshoot = speedConfig.BOUNCE_OVERSHOOT;

    // Landing duration: an explicit LANDING_DURATION wins (TURBO/QUICK);
    // otherwise it is derived from the current velocity, so a tension-slowed
    // reel gets a longer, gentler landing with the same bounce.
    let stopDuration;
    const landingDuration = speedConfig.LANDING_DURATION || null;
    if (landingDuration) {
      // Fixed landing duration (TURBO/QUICK)
      stopDuration = landingDuration;
    } else {
      // Velocity-matched: landing adapts to current spin speed (including tension deceleration)
      const effectiveVelocity = this.normalizedSpinSpeed
        * (this._tensionDecelerating ? this._tensionSpeedMultiplier : 1.0);
      const velocityMatchedDuration =
        ((overshoot + 3) * adjustedLandingDistance * 1000) /
        effectiveVelocity;
      stopDuration = velocityMatchedDuration * (speedConfig.STOP_SLOWDOWN_FACTOR ?? 1.0);
      // Tension landing: stretch duration to compensate for overshoot appearing faster against slow spin.
      // Per-speed override (speedConfig.TENSION_LANDING_SLOWDOWN) takes priority over global config.
      // Turbo/super_turbo set 1.0 (no slowdown) since their decelerated velocity is still fast enough.
      if (this._tensionDecelerating) {
        const tensionLandingSlowdown = speedConfig.TENSION_LANDING_SLOWDOWN
          ?? FEATURES_CONFIG.TENSION.REEL_DECELERATION.TENSION_LANDING_SLOWDOWN
          ?? 1.0;
        stopDuration *= tensionLandingSlowdown;
      }
    }

    this._setLandingSymbolOnVisibleSprites();

    // Landing symbols are above the visible area — set them to normal (non-blurred)
    // textures immediately so they're already sharp when they scroll into view
    {
      const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;
      for (let i = 0; i < landingZoneEnd; i++) {
        const spriteData = this.spinningSprites[i];
        if (!spriteData) continue;
        spriteData.normalSprite.alpha = 1;
        if (spriteData.blurredSprite) spriteData.blurredSprite.alpha = 0;
        if (spriteData.valueText && spriteData.valueText.text !== "") {
          spriteData.valueText.alpha = 1;
        }
        if (spriteData.valueTextBlurred) {
          spriteData.valueTextBlurred.alpha = 0;
        }
      }

      this.isBlurred = false;
    }

    if (this.reels && this.reels.length > 1) {
      this.symbolId = this.reels[1];
    }

    // Stop params for the ticker loop (see updateSymbolsAbsolute)
    this._stopElapsed = 0;
    this._stopDistance = adjustedLandingDistance;
    this._stopDuration = stopDuration;
    this._stopOvershoot = overshoot;
    this._stopLastEasedPos = 0;

    this.stopAnimationProgress = 0;
    this.stopAnimationTotalDistance = adjustedLandingDistance;
    this._landingPeakFired = false; // Reset peak detection for this landing
    this.isPlaying = false;
    this.isDecelerating = false;
  }

  // Shared landing finalization — called when ticker-based stop animation completes
  _finalizeLanding() {
    // Remove ticker — stop animation is done
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    // Clear stop animation params
    this._stopElapsed = 0;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;
    this.activeStopAnimation = null;
    this.stopAnimationProgress = 0;
    this.isDecelerating = false;

    // Reset tension deceleration state
    this._tensionDecelerating = false;
    this._tensionDecelElapsed = 0;
    this._tensionSpeedMultiplier = 1.0;

    // Rotate array: move last N sprites to front (single O(n) operation)
    const cut = this.spinningSprites.length - STOP_MOVE_DISTANCE_SYMBOLS;
    const tail = this.spinningSprites.splice(cut);
    this.spinningSprites.unshift(...tail);

    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }
    this._applyRowOrder();

    // Set critical state flags immediately
    this.hasStopped = true;
    this.stopTweenActive = false;
    // Bookkeeping only: the loop above placed every sprite absolutely, so a
    // delta shift here would move them a second time.
    this.reelYOffset = 0;
    this.lockedSpinSpeed = null;
    this.currentVelocity = 0;

    // Fire landing animations first, before the heavier work below.
    if (this.onReelStop) {
      this.onReelStop(this.reelIndex, this.reels);
    }

    // Fire onReelLanded on the NEXT frame — gives landing animations an uncontested
    // render frame before tension operations (dim, sound, spine) kick in
    if (this.onReelLanded) {
      const callback = this.onReelLanded;
      const reelIndex = this.reelIndex;
      requestAnimationFrame(() => callback(reelIndex));
    }

    // Unblur all remaining sprites (bottom buffer etc.)
    this._switchToNormalTextures();

    this._hideBufferSprites();

    // Landing sounds. A landing with overshoot already played its sound at
    // the peak; turbo and manual stop skip the peak and are handled here.
    const isTurboMode = isTurboLike(this.autoPlaySpeed);
    const isTurboSimultaneousLanding = isTurboMode && !this.isTensionMode;
    const isManualStop = this._wasManualStopped;
    if (isTurboSimultaneousLanding) {
      // Turbo/super_turbo: reel_stop ONCE, on the first reel; all land together.
      // (There is no quick_stop sound file, so reel_stop plays that role.)
      if (this.reelIndex === 0) {
        // soundManager.play(SOUND_NAME.reel_stop);
        // quick_stop has its own file now (2026-10-20).
        soundManager.play(SOUND_NAME.quick_stop);
      }
    } else if (isManualStop) {
      // Manual stop: once, on the first reel that was force-stopped.
      if (!ReelControllerV2._manualStopSoundPlayed) {
        ReelControllerV2._manualStopSoundPlayed = true;
        // soundManager.play(SOUND_NAME.reel_stop);
        soundManager.play(SOUND_NAME.quick_stop);
      }
    } else if (!this._landingPeakFired) {
      // A landing with NO overshoot never reaches the peak, so its sound is
      // owed here instead. Thud or announcement, same rule.
      this._playLandingSound();
    }

    // A manual stop skips the peak and already has its thud above, so add
    // only the scatter/mystery announcement.
    if (isManualStop) {
      this._playLandingSound({ thud: false });
    }

    // Turbo makes no per-symbol announcements, except the mystery: its reveal
    // stops the board at any speed, so it must not be silent. Checked with
    // _hasLandedMystery because _getReelStopSound would answer scatter first.
    if (isTurboSimultaneousLanding && this._hasLandedMystery()) {
      soundManager.playOncePerFrame(SOUND_NAME.mystery_land);
    }

    // A landing with NO overshoot (e.g. super turbo) never reaches the peak,
    // so report it now. The anticipation chain waits on onLandingPeak; without
    // it the reels it holds would spin for ever.
    if (!this._landingPeakFired) {
      this._landingPeakFired = true;
      this.onLandingPeak?.(this.reelIndex);
    }

    if (this.reelIndex === Settings.REEL_COUNT - 1) {
      soundManager.stop(SOUND_NAME.reel_spin);
      this._completeSpin();
    }
  }

  /**
   * Dispatch setSpinComplete, but only once the board has stopped moving.
   *
   * Its reducer clears `isSpinning`, which reopens the spin button, releases
   * the win presentation and lets autoplay fire. Sent while landing bounces
   * are still playing, a new spin would start on top of them and clear the
   * spine track they play on. Holding this one signal gives every consumer
   * the same answer to "is the round finished".
   *
   * `onReelStop` runs just before this, so the landings are already
   * registered. With no landings (turbo etc.) the wait resolves on the next
   * microtask; waitForBoardSettle has its own stall guard.
   */
  _completeSpin() {
    const wait = this.waitForBoardSettle;
    if (typeof wait !== "function") {
      this.dispatch(setSpinComplete(true));
      return;
    }
    wait().then(() => {
      this.dispatch(setSpinComplete(true));
    });
  }

  // Set landing symbol - typically called with API data during spin
  setLandingSymbol(symbolId) {
    if (symbolId === undefined || symbolId === null) {
      return;
    }

    if (this.isPlaying && this.spinningSprites.length > 0) {
      if (this._isLandingSpriteVisible()) {
        this.pendingLandingSymbolId = symbolId;
        this.symbolId = symbolId;
      } else {
        this.symbolId = symbolId;
        this.pendingLandingSymbolId = null;
        this._setLandingSymbolOnVisibleSprites(symbolId);
      }
      return;
    }

    this.symbolId = symbolId;
    this.updateSymbolTexture(symbolId);
  }

  // ── Buffer sprite visibility ──

  // Hides buffer sprites (above/below visible area) when reels are idle
  _hideBufferSprites() {
    if (!FEATURES_CONFIG.SPIN.HIDE_IDLE_BUFFERS) return;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      const outsideWindow =
        i < VISIBLE_SYMBOL_START_INDEX ||
        i >= VISIBLE_SYMBOL_START_INDEX + VISIBLE_SYMBOL_COUNT;
      // Window sprites are shown explicitly: the per-frame cull may have
      // hidden any of them while the strip moved.
      this.spinningSprites[i].container.visible = !outsideWindow;
    }
  }

  /**
   * Hide the sprites that are outside the window THIS FRAME, while the strip
   * moves. The strip is SYMBOLS_VISIBLE * 2 + 2 cells long (the landing stages
   * a whole board above the window), so about half of it is always masked
   * away; culling saves submitting it. Changes only `visible`.
   */
  _cullOffscreenSprites() {
    // Same flag as _hideBufferSprites/_showBufferSprites, the only code that
    // sets `visible` back to true; culling without it would leave sprites
    // hidden for good.
    if (!FEATURES_CONFIG.SPIN.HIDE_IDLE_BUFFERS) return;

    const h = this.symbolHeight;
    if (!h) return;

    // The window in sprite-local y: visible cells sit at y = i * h for
    // i in VISIBLE_SYMBOL_START_INDEX .. + VISIBLE_SYMBOL_COUNT, each reaching
    // half a cell either side, so a sprite overlaps while within one cell of
    // that range. The margin covers ReelSetMask's padding several times over.
    const margin = h * 0.1;
    const top = (VISIBLE_SYMBOL_START_INDEX - 1) * h - margin;
    // During the stop, the sprites that park on buffer slots must never be
    // drawn (their art overflows the cell into the edge rows, then pops when
    // _hideBufferSprites runs):
    //   bottom   strict: a sprite leaving the window goes once its cell is out;
    //   index 0  never drawn: it parks as the top buffer, and the bounce only
    //            dips it into the window for a few frames.
    // The top keeps its margin so the landing rows still slide in smoothly.
    const strictBottom = (VISIBLE_SYMBOL_START_INDEX + VISIBLE_SYMBOL_COUNT) * h - h * 0.01;
    const bottom = this.stopTweenActive
      ? strictBottom
      : (VISIBLE_SYMBOL_START_INDEX + VISIBLE_SYMBOL_COUNT) * h + margin;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      const container = this.spinningSprites[i]?.container;
      if (!container) continue;
      const onScreen =
        container.y > top &&
        container.y < bottom &&
        !(this.stopTweenActive && i === 0);
      if (container.visible !== onScreen) container.visible = onScreen;
    }
  }

  // Shows all buffer sprites before spin starts (restores full strip for animation)
  _showBufferSprites() {
    if (!FEATURES_CONFIG.SPIN.HIDE_IDLE_BUFFERS) return;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.visible = true;
    }
  }

  // ── Texture visibility (for overlay spine animations) ──

  // Hides symbol texture to show overlay spine animation
  hideSymbolTexture(symbolIndex) {
    const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
    if (spriteIndex >= this.spinningSprites.length) return;

    const sprite = this.spinningSprites[spriteIndex];
    if (!sprite) return;

    // Alpha 0, not 0.001: an overlay spine owns the cell, and at 0 PIXI skips
    // the sprite instead of rendering it. showSymbolTexture puts it back at 1.
    if (sprite.normalSprite) {
      sprite.normalSprite.alpha = 0;
    }
    if (sprite.blurredSprite) {
      sprite.blurredSprite.alpha = 0;
    }
    if (sprite.valueText) {
      sprite.valueText.alpha = 0;
    }
    if (sprite.valueTextBlurred) {
      sprite.valueTextBlurred.alpha = 0;
    }
  }

  // Shows symbol texture (hides overlay animation)
  showSymbolTexture(symbolIndex) {
    const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
    if (spriteIndex >= this.spinningSprites.length) return;

    const sprite = this.spinningSprites[spriteIndex];
    if (!sprite) return;

    // A COMPOSITE is not its symbol: `currentSymbolId` still names the closed
    // gamble scatter, so scale it as a composite (see _applyCompositeScale).
    if (sprite._staticOverride) {
      this._applyCompositeScale(sprite);
    } else if (sprite.currentSymbolId !== null) {
      const info = symbolData[sprite.currentSymbolId];
      if (info?.name) {
        const scale = this._calculateSymbolScale(
          info.name,
          this.symbolWidth,
          this.symbolHeight,
          sprite.currentSymbolId,
        );
        if (sprite.normalSprite) {
          sprite.normalSprite.scale.set(scale);
        }
        if (sprite.blurredSprite) {
          sprite.blurredSprite.scale.set(scale);
        }
      }
    }

    if (sprite.normalSprite) {
      sprite.normalSprite.alpha = 1;
    }
    if (sprite.blurredSprite) {
      sprite.blurredSprite.alpha = 0;
    }
    if (sprite.valueText && sprite.valueText.text !== "") {
      sprite.valueText.alpha = 1;
    }
    if (sprite.valueTextBlurred) {
      sprite.valueTextBlurred.alpha = 0;
    }
  }

  // Shows all visible symbol textures (clears overlay animations)
  showAllSymbolTextures() {
    const start = VISIBLE_SYMBOL_START_INDEX;
    const end = start + VISIBLE_SYMBOL_COUNT;

    for (let i = start; i < end && i < this.spinningSprites.length; i++) {
      const sprite = this.spinningSprites[i];
      if (sprite) {
        // Recalculate scale (an expanding wild animation may have changed it)
        if (sprite.currentSymbolId !== null) {
          const info = symbolData[sprite.currentSymbolId];
          if (info?.name) {
            const scale = this._calculateSymbolScale(
              info.name,
              this.symbolWidth,
              this.symbolHeight,
              sprite.currentSymbolId,
            );
            if (sprite.normalSprite) {
              sprite.normalSprite.scale.set(scale);
            }
            if (sprite.blurredSprite) {
              sprite.blurredSprite.scale.set(scale);
            }
          }
        }

        if (sprite.normalSprite) {
          sprite.normalSprite.alpha = 1;
        }
        if (sprite.blurredSprite) {
          sprite.blurredSprite.alpha = 0;
        }
        if (sprite.valueText && sprite.valueText.text !== "") {
          sprite.valueText.alpha = 1;
        }
        if (sprite.valueTextBlurred) {
          sprite.valueTextBlurred.alpha = 0;
        }
      }
    }

    this._restoreStaticOverrides();
  }

  // Exposes sprites in format compatible with useWinController - returns only visible sprites
  get symbolsContainer() {
    const visibleStart = VISIBLE_SYMBOL_START_INDEX;
    const visibleEnd = visibleStart + VISIBLE_SYMBOL_COUNT;
    const visibleSprites = this.spinningSprites.slice(visibleStart, visibleEnd);

    return {
      children: visibleSprites.map((sprite, overlayIndex) => ({
        currentSymbolId: sprite.currentSymbolId,
        symbolId: sprite.currentSymbolId,
        overlayIndex: overlayIndex, // Direct mapping: 0, 1, 2, 3
        // The cell shows a SETTLED picture (see setSymbolTextureOnly), not the
        // symbol its id names — so nothing may play that symbol's spine on it.
        hasStaticOverride: !!sprite._staticOverride,
      })),
    };
  }

  // ── Expanding wild support ──

  // Replaces all visible symbols with target symbol (e.g., wild expansion)
  // @param {number} targetSymbolId - Symbol ID to replace with
  // @param {boolean} hideTextures - If true, hides textures (for cascade mode). If false, keeps visible (for full-reel mode)
  replaceVisibleSymbols(targetSymbolId, hideTextures = true) {
    const symbolInfo = symbolData[targetSymbolId];
    if (!symbolInfo?.texture) return;

    const textureName = symbolInfo.texture;
    const texture = this.spritesheet?.textures?.[`${textureName}.png`];
    if (!texture) return;

    // Replace all visible sprites
    const start = VISIBLE_SYMBOL_START_INDEX;
    const end = start + VISIBLE_SYMBOL_COUNT;

    for (let i = start; i < end && i < this.spinningSprites.length; i++) {
      const sprite = this.spinningSprites[i];
      if (!sprite) continue;

      sprite.normalSprite.texture = texture;
      sprite.currentSymbolId = targetSymbolId;
      this._applySpriteZIndex(sprite, targetSymbolId);

      // Only hide textures if requested (cascade mode needs this, full-reel mode doesn't)
      if (hideTextures) {
        sprite.normalSprite.alpha = 0.001;
        sprite.blurredSprite.alpha = 0;
      }
    }
  }

  // Replaces single symbol at position (for cascading wild animation)
  replaceSymbolAtPosition(symbolIndex, targetSymbolId) {
    const symbolInfo = symbolData[targetSymbolId];
    if (!symbolInfo?.texture) return;

    // Update the reels array too, so the next spin starts from the wild
    // without blinking. New array: the original may be frozen Redux state.
    if (this.reels && this.reels.length > symbolIndex) {
      this.reels = this.reels.map((id, idx) =>
        idx === symbolIndex ? targetSymbolId : id,
      );
    }

    const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
    if (spriteIndex >= this.spinningSprites.length) return;

    const sprite = this.spinningSprites[spriteIndex];
    if (!sprite) return;

    // Clear currentSymbolId to bypass _setSpriteTexture's early return
    sprite.currentSymbolId = null;

    // Full repaint (normal, blurred, coin value) so showSymbolTexture() later
    // reveals the wild
    this._setSpriteTexture(sprite, targetSymbolId);

    // Then hide the textures for the animation overlay to show
    sprite.normalSprite.alpha = 0.001;
    sprite.blurredSprite.alpha = 0;
  }

  /**
   * Swap ONLY the picture at a position; the cell keeps its identity.
   *
   * Unlike `replaceSymbolAtPosition` (the expanding wild, where the cell must
   * BECOME a wild for useWinController), a reveal leaves the gamble scatter a
   * gamble scatter: it must still celebrate on the `scg` spine with the
   * others, so `currentSymbolId` and `this.reels` are not touched. Only the
   * static texture underneath changes.
   *
   * Alpha and zIndex are not touched, so a hidden cell stays hidden. A
   * composite override is scaled via _applyCompositeScale.
   */
  setSymbolTextureOnly(symbolIndex, textureSymbolId, textureOverride = null) {
    const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
    if (spriteIndex >= this.spinningSprites.length) return;

    const sprite = this.spinningSprites[spriteIndex];
    if (!sprite) return;

    // A ready-made COMPOSITE picture wins: the gamble scatter's settled cell
    // (open window with card, pips and spin count baked in). It has no
    // symbolData id. Anything else comes off symbolData.
    if (textureOverride) {
      // Recorded in `_staticOverride` because repaints from the cell's
      // identity (e.g. the bonus transition's snapClearAll) would put the
      // closed door back; _restoreStaticOverrides re-applies it. Cleared only
      // when the sprite recycles (see _setSpriteTexture).
      sprite._staticOverride = textureOverride;
      if (sprite.normalSprite) sprite.normalSprite.texture = textureOverride;
      // The blurred twin is only seen for the first frames of the next spin,
      // so the sharp picture serves for it too.
      if (this.spritesheetBlurred && sprite.blurredSprite) {
        sprite.blurredSprite.texture = textureOverride;
      }
      // Swapping `.texture` leaves `scale` alone, so set it explicitly.
      this._applyCompositeScale(sprite);
      return;
    }

    const info = symbolData[textureSymbolId];
    if (!info?.texture) return;

    const texture = this.spritesheet?.textures?.[`${info.texture}.png`];
    if (!texture) return;

    sprite._staticOverride = texture;
    if (sprite.normalSprite) sprite.normalSprite.texture = texture;
    if (this.spritesheetBlurred && sprite.blurredSprite) {
      sprite.blurredSprite.texture =
        this.spritesheetBlurred.textures?.[`${info.texture}_blurred.png`] ||
        PIXI.Texture.EMPTY;
    }
  }

  /**
   * Put back any texture-only override a repaint has just undone.
   *
   * Called from showAllSymbolTextures, which is what the bonus transition's
   * snapClearAll ends with. See setSymbolTextureOnly for why the override has
   * to survive that at all.
   */
  _restoreStaticOverrides() {
    const start = VISIBLE_SYMBOL_START_INDEX;
    const end = start + VISIBLE_SYMBOL_COUNT;
    for (let i = start; i < end && i < this.spinningSprites.length; i++) {
      const sprite = this.spinningSprites[i];
      const override = sprite?._staticOverride;
      if (!override) continue;
      if (sprite.normalSprite) sprite.normalSprite.texture = override;
      if (this.spritesheetBlurred && sprite.blurredSprite) {
        sprite.blurredSprite.texture = override;
      }
      // The repaint this is undoing rescaled the sprite to its symbol id.
      this._applyCompositeScale(sprite);
    }
  }

  // ── Cleanup ──

  clearAllTimeouts() {
    // Stop and clear all anime.js animations
    if (this.windUpAnimation) {
      this.windUpAnimation.pause();
      anime.remove(this._reelYTweenTarget); // Force-eject from anime.js internal tracker
      this.windUpAnimation = null;
    }

    if (this.activeStopAnimation) {
      this.activeStopAnimation.pause();
      anime.remove(this.activeStopAnimation); // Force-eject from anime.js internal tracker
      this.activeStopAnimation = null;
    }

    // Remove PIXI.Ticker callback
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }
  }

  _cleanupSpinningSprites() {
    if (this.spinningSprites.length) {
      this.spinningSprites.forEach((symbolData) => {
        try {
          if (symbolData.container?.parent && this.spinningContainer) {
            this.spinningContainer.removeChild(symbolData.container);
          }
          if (symbolData.normalSprite && !symbolData.normalSprite.destroyed) {
            symbolData.normalSprite.destroy();
          }
          if (symbolData.blurredSprite && !symbolData.blurredSprite.destroyed) {
            symbolData.blurredSprite.destroy();
          }
          if (symbolData.valueText && !symbolData.valueText.destroyed) {
            symbolData.valueText.destroy();
          }
          if (
            symbolData.valueTextBlurred &&
            !symbolData.valueTextBlurred.destroyed
          ) {
            symbolData.valueTextBlurred.destroy();
          }
          if (symbolData.container && !symbolData.container.destroyed) {
            symbolData.container.destroy();
          }
        } catch (e) {
          // ignore
        }
      });
      this.spinningSprites = [];
    }

    this.isPlaying = false;
    this.hasStopped = false;
  }
}

// Static flag: the manual-stop sound plays only once across all reels
ReelControllerV2._manualStopSoundPlayed = false;

export default ReelControllerV2;
